import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { DatePicker, Dropdown, Progress, Tooltip, message } from "antd";
import moment from "moment";
import ProjectServices from "../../../Services/ProjectServices";
import DataParser from "../../../utlits/Dataparser";

// Shown after a project is created with "Generate tasks with AI": Gemini reads the attached
// document and streams its tasks back (real progress: tasks received / tasks planned). The user
// then reviews them with the same fields as the Create Task form for the company's task mode and
// saves the kept ones (the server runs each through the regular Create Task handler).
//   "engine" (Advanced Task Scheduling): Normal/Critical + effort with unit, no date (scheduler dates it)
//   "manual": due date, no effort
// The look is the Create Task modal's: same modal shell, floating title/description, pills,
// dropdowns, prompt cards and buttons.

// Same options, colours and conversion as the Create Task form
const STATUS_OPTIONS = [
    { value: "Pending", label: "Pending", color: "#DFA70D" },
    { value: "In-Progress", label: "In Progress", color: "#607EF9" },
    { value: "On-Hold", label: "On Hold", color: "#0DDFAB" },
    { value: "Complete", label: "Complete", color: "#1CB333" },
];
const APPROVAL_OPTIONS = [{ value: 1, label: "Yes" }, { value: 0, label: "No" }];
const UNIT_MINUTES = { minutes: 1, hours: 60, days: 8 * 60 };
const UNIT_OPTIONS = [{ value: "minutes", short: "Mins", label: "Minutes" }, { value: "hours", short: "Hrs", label: "Hours" }, { value: "days", short: "Days", label: "Days" }];
const PRIORITY_OPTIONS = [{ value: "normal", label: "Normal", color: "#0DDFAB" }, { value: "critical", label: "Critical", color: "#FF4D4F" }];

// Above the Create Project modal; menus, date pickers, tooltips and prompts above this
const MODAL_Z = 100010;
const POPUP_Z = 100030;

const initials = (name) => name.split(" ").map((n) => n[0]).join("").toUpperCase().substring(0, 2);

// The Create Task form's pill icons (masked SVGs from /assets/images)
const MaskIcon = ({ name, color, className }) => (
    <span className={className} style={{
        display: "inline-block", width: "15px", height: "15px", flexShrink: 0, backgroundColor: color,
        mask: `url('/assets/images/${name}') no-repeat center / contain`, WebkitMask: `url('/assets/images/${name}') no-repeat center / contain`,
    }} />
);

// Opens a Create Task dropdown at this element. Positioned by antd (rc-trigger) like every other
// popup in the app: it renders on <body>, so the scrolling task list never clips it, flips up when
// there is no room below, and follows the page zoom set in public/index.html.
const MenuAnchor = ({ id, menu, setMenu, disabled, title, render, children, ...props }) => (
    <Dropdown
        trigger={["click"]}
        disabled={disabled}
        open={menu === id}
        onOpenChange={(open) => setMenu(open ? id : null)}
        overlayClassName="ai-review-popup"
        overlayStyle={{ zIndex: POPUP_Z }}
        dropdownRender={() => render(() => setMenu(null))}
    >
        <div {...props}>
            {title ? (
                <Tooltip title={title} placement="top" overlayStyle={{ zIndex: POPUP_Z }}>
                    <span style={{ display: "flex", alignItems: "center", gap: "inherit" }}>{children}</span>
                </Tooltip>
            ) : children}
        </div>
    </Dropdown>
);

// Create Task "Assigned to" dropdown
const UserMenu = ({ options, onSelect }) => {
    const [search, setSearch] = useState("");
    const shown = options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()));
    return (
        <div className="custom-user-dropdown">
            <div className="search-header">
                <i className="uil uil-search" />
                <input autoFocus placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <div className="user-list-scroll">
                {shown.length > 0 ? shown.map((o) => (
                    <div key={o.value} className="user-option-item" onClick={() => onSelect(o.value)}>
                        <div className="user-avatar-mini">{initials(o.label)}</div>
                        <span>{o.label}</span>
                    </div>
                )) : <div className="no-users-found">No members found</div>}
            </div>
        </div>
    );
};

// Create Task "Task Status" / "Approval Required" dropdown
const OptionMenu = ({ options, width, onSelect }) => (
    <div className="custom-priority-dropdown" style={{ width }}>
        <div className="priority-list-scroll">
            {options.map((o) => (
                <div key={String(o.value)} className="priority-option-item" onClick={() => onSelect(o.value)}>
                    {o.color && <span style={{ backgroundColor: o.color, width: "8px", height: "8px", borderRadius: "50%", marginRight: "8px" }} />}
                    <span>{o.label}</span>
                </div>
            ))}
        </div>
    </div>
);

// Create Task priority / effort-unit dropdown (inline-styled there too)
const PlainMenu = ({ options, selected, minWidth, fontSize, onSelect }) => (
    <div style={{ background: "#fff", border: "1px solid #E8E8E8", borderRadius: "8px", boxShadow: "0 4px 12px rgba(0,0,0,0.12)", minWidth, overflow: "hidden" }}>
        {options.map((o) => (
            <div
                key={o.value}
                onClick={() => onSelect(o.value)}
                style={{
                    display: "flex", alignItems: "center", gap: "8px", padding: "8px 12px", cursor: "pointer", fontSize,
                    fontWeight: selected === o.value ? "600" : "400", background: selected === o.value ? "#f5f5f5" : "#fff",
                    color: o.value === "critical" ? "#FF4D4F" : "#1D1D1D",
                }}
            >
                {o.color && <span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: o.color, flexShrink: 0 }} />}
                {o.label}
            </div>
        ))}
    </div>
);

// Create Task prompt card (as the "Set Critical Priority" prompt)
const PromptCard = ({ title, children, onClose, actions }) => ReactDOM.createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: POPUP_Z, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.35)" }} onClick={onClose}>
        <div style={{ background: "#fff", borderRadius: "12px", padding: "24px", width: "400px", maxWidth: "92vw", maxHeight: "80vh", overflowY: "auto", boxShadow: "0 8px 32px rgba(0,0,0,0.18)", position: "relative" }} onClick={(e) => e.stopPropagation()}>
            <button
                type="button"
                style={{ position: "absolute", top: "12px", right: "12px", border: "none", background: "transparent", cursor: "pointer", fontSize: "20px", color: "#888", lineHeight: 1, padding: "0" }}
                onClick={onClose}
            >×</button>
            <div style={{ fontWeight: "700", fontSize: "15px", marginBottom: "8px", color: "#1D1D1D" }}>{title}</div>
            <div style={{ fontSize: "13px", color: "#555", marginBottom: "20px", lineHeight: "1.5" }}>{children}</div>
            <div className="d-flex justify-content-end gap-2">{actions}</div>
        </div>
    </div>,
    document.body
);

// Centred card shown while the AI works and there is nothing to list yet (the modal is compact then)
const LoadingCard = ({ title, detail, percent }) => (
    <div className="text-center px-3 pt-2 pb-3">
        <div className="ai-loader"><i className="uil uil-file-alt" /></div>
        <div style={{ fontSize: "15px", fontWeight: "700", color: "#1D1D1D", marginTop: "16px" }}>{title}</div>
        <div style={{ fontSize: "12px", color: "#888", marginTop: "4px" }}>{detail}</div>
        {percent != null && (
            <div style={{ maxWidth: "360px", margin: "14px auto 0" }}>
                <Progress percent={percent} status="active" strokeColor="#0DDFAB" showInfo={false} />
            </div>
        )}
    </div>
);

const AiTaskReviewModal = ({ projectId, attachmentId, projectName, detail = "high", onClose }) => {
    const [phase, setPhase] = useState("reading"); // reading | writing | done | error
    const [total, setTotal] = useState(0);
    const [tasks, setTasks] = useState([]);
    const [error, setError] = useState(null);
    const [documentName, setDocumentName] = useState("");
    const [newProjectDue, setNewProjectDue] = useState(null);
    const [projectDue, setProjectDue] = useState(null); // real deadline (YYYY-MM-DD) or null when none set
    const [openQuestions, setOpenQuestions] = useState([]);
    const [scheduling, setScheduling] = useState("manual");
    // People this user may assign (same hierarchy rule as Create Task), sent by the server
    const [assignees, setAssignees] = useState([]);
    const [creating, setCreating] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const [showQuestions, setShowQuestions] = useState(false);
    const [menu, setMenu] = useState(null);       // id of the open dropdown
    const [prompt, setPrompt] = useState(null);   // { type: "regenerate" } | { type: "failed", items, data }
    const [invalid, setInvalid] = useState({});   // task key -> missing fields (red pills, as Create Task)
    const [formError, setFormError] = useState("");
    // Step 1, before any task is generated: the questions the document leaves open. The user's
    // answers go into the task generation (unanswered ones fall back to the simplest assumption).
    const [step, setStep] = useState("questions-loading"); // questions-loading | questions | tasks
    const [questions, setQuestions] = useState([]);         // [{ question, options }]
    const [answers, setAnswers] = useState({});              // question index -> answer text
    const [qIndex, setQIndex] = useState(0);                 // the question on screen (one per step)
    const listEndRef = useRef(null);
    const selectAllRef = useRef(null);
    // The finished review (with the user's edits) is kept for this tab, so a remount, reload or
    // reopen restores it instead of running the AI again. Cleared once the tasks are created.
    const cacheKey = `planit_ai_review_${projectId}_${attachmentId}_${detail}`;
    const freshRunRef = useRef(false); // set by "Regenerate": skip both caches once
    const questionsKey = `planit_ai_questions_${projectId}_${attachmentId}`;

    // Step 1: load the questions (or restore them / a finished review for this tab)
    useEffect(() => {
        let hasReview = false;
        try { hasReview = !!JSON.parse(sessionStorage.getItem(cacheKey))?.tasks; } catch (e) { /* no saved review */ }
        try {
            const saved = JSON.parse(sessionStorage.getItem(questionsKey));
            if (saved?.questions) {
                setQuestions(saved.questions);
                setAnswers(saved.answers || {});
                setQIndex(Math.min(saved.qIndex || 0, Math.max(saved.questions.length - 1, 0)));
                setStep(hasReview || saved.submitted || saved.questions.length === 0 ? "tasks" : "questions");
                return undefined;
            }
        } catch (e) { /* no saved questions */ }
        if (hasReview) { setStep("tasks"); return undefined; }
        let cancelled = false;
        ProjectServices.aiDocumentQuestions(projectId, attachmentId)
            .then((res) => {
                if (cancelled) return;
                const qs = res?.Good ? (res.data?.questions || []) : [];
                if (res?.Good) setDocumentName(res.data?.document || "");
                setQuestions(qs);
                setStep(qs.length ? "questions" : "tasks"); // nothing unclear: straight to the tasks
            })
            .catch((err) => {
                if (cancelled) return;
                console.error("Could not load AI questions:", err);
                setStep("tasks"); // the questions are optional; the task step reports real errors
            });
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId, attachmentId]);

    // Keep the questions and answers for this tab (submitted = tasks were generated from them)
    useEffect(() => {
        if (step === "questions-loading") return;
        try {
            sessionStorage.setItem(questionsKey, JSON.stringify({ questions, answers, qIndex, submitted: step === "tasks" }));
        } catch (e) { /* not restorable, still works */ }
    }, [questionsKey, step, questions, answers, qIndex]);

    const answeredList = () => questions
        .map((q, i) => ({ question: q.question, answer: String(answers[i] || "").trim() }))
        .filter((a) => a.answer);

    // Generate the tasks from the current answers (a saved review from older answers is dropped)
    const startTasks = () => {
        try { sessionStorage.removeItem(cacheKey); } catch (e) { /* ignore */ }
        setAttempt((a) => a + 1);
        setStep("tasks");
    };
    const editAnswers = () => {
        try { sessionStorage.removeItem(cacheKey); } catch (e) { /* ignore */ }
        setTasks([]);
        setQIndex(0);
        setStep("questions");
    };

    useEffect(() => {
        if (step !== "tasks") return undefined;
        const fresh = freshRunRef.current;
        freshRunRef.current = false;
        if (!fresh) {
            try {
                const saved = JSON.parse(sessionStorage.getItem(cacheKey));
                if (saved?.tasks) {
                    setTasks(saved.tasks);
                    setAssignees(saved.assignees || []);
                    setDocumentName(saved.documentName || "");
                    setNewProjectDue(saved.newProjectDue || null);
                    setProjectDue(saved.projectDue || null);
                    setOpenQuestions(saved.openQuestions || []);
                    setScheduling(saved.scheduling || "manual");
                    setPhase("done");
                    return undefined;
                }
            } catch (e) { /* no usable saved review: analyze */ }
        }
        const controller = new AbortController();
        setPhase("reading");
        setTotal(0);
        setTasks([]);
        setError(null);
        setInvalid({});
        setFormError("");
        ProjectServices.aiAnalyzeDocumentStream(projectId, attachmentId, detail, (ev) => {
            if (ev.type === "stage") {
                setPhase(ev.stage);
                if (ev.scheduling) setScheduling(ev.scheduling);
            }
            else if (ev.type === "count") { setTotal(ev.total); setPhase("writing"); }
            else if (ev.type === "task") setTasks((prev) => [...prev, { ...ev.task, key: prev.length, keep: true }]);
            else if (ev.type === "done") {
                // Defaults: assigned to the project owner (else me, if I may assign them / myself),
                // Pending, no approval, Normal; effort starts from the AI's estimate in hours
                const people = ev.assignees || [];
                const canAssign = (id) => id && people.some((u) => Number(u.id) === Number(id));
                const owner = Number(ev.project_owner) || null;
                const me = Number(DataParser.UserId());
                const defaultAssignee = canAssign(owner) ? owner : canAssign(me) ? me : (people[0]?.id ?? null);
                setTasks((ev.tasks || []).map((t, i) => ({
                    ...t,
                    key: i,
                    keep: true,
                    assign_to: defaultAssignee,
                    status: "Pending",
                    approval_required: 0,
                    task_priority: "normal",
                    effort_value: t.effort_hours,
                    effort_unit: "hours",
                })));
                setAssignees(people);
                setDocumentName(ev.document || "");
                setNewProjectDue(ev.new_project_due || null);
                setProjectDue(ev.project_due || null);
                setOpenQuestions(ev.open_questions || []);
                if (ev.scheduling) setScheduling(ev.scheduling);
                setPhase("done");
            } else if (ev.type === "error") { setError(ev.message); setPhase("error"); }
        }, controller.signal, fresh, answeredList()).catch((err) => {
            if (controller.signal.aborted) return;
            setError(err.message || "The AI could not analyze this document.");
            setPhase("error");
        });
        return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId, attachmentId, detail, attempt, step]);

    // Save the finished review, including every edit, for this tab
    useEffect(() => {
        if (phase !== "done") return;
        try {
            sessionStorage.setItem(cacheKey, JSON.stringify({ tasks, assignees, documentName, newProjectDue, projectDue, openQuestions, scheduling }));
        } catch (e) { /* storage full or blocked: the review still works, just isn't restorable */ }
    }, [cacheKey, phase, tasks, assignees, documentName, newProjectDue, projectDue, openQuestions, scheduling]);

    // Page behind stays still while the review is open
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, []);

    // The footer's "Assign all" menu closes on a click anywhere else (antd dropdowns do this themselves)
    useEffect(() => {
        if (menu !== "all:assign") return undefined;
        const onDown = (e) => { if (!e.target.closest(".ai-assign-all")) setMenu(null); };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [menu]);

    // Keep the newest streamed task in view while the AI is writing
    useEffect(() => {
        if (phase === "writing") listEndRef.current?.scrollIntoView({ block: "nearest" });
    }, [tasks.length, phase]);

    const update = (key, changes) => {
        setTasks((prev) => prev.map((t) => (t.key === key ? { ...t, ...changes } : t)));
        if (invalid[key]) setInvalid((prev) => { const next = { ...prev }; delete next[key]; return next; });
        setFormError("");
    };

    // Switching unit keeps the same amount of work, like the Create Task form
    const changeUnit = (t, unit) => {
        const minutes = Number(t.effort_value || 0) * UNIT_MINUTES[t.effort_unit || "hours"];
        const value = unit === "minutes" ? Math.round(minutes) : parseFloat((minutes / UNIT_MINUTES[unit]).toFixed(2));
        update(t.key, { effort_unit: unit, effort_value: value });
    };

    const engine = scheduling === "engine";
    const editable = phase === "done";
    const assigneeOptions = assignees.map((u) => ({ value: Number(u.id), label: u.name || `User ${u.id}` }));
    const kept = tasks.filter((t) => t.keep);
    const allKept = tasks.length > 0 && tasks.every((t) => t.keep);
    const percent = phase === "done" ? 100
        : phase === "reading" ? 5
        : total > 0 ? Math.min(99, Math.round(10 + (tasks.length / total) * 89)) : 10;
    // Nothing to list yet: the modal shrinks to a compact loading card instead of a mostly empty page
    const waiting = step === "questions-loading" || (step === "tasks" && phase !== "done" && phase !== "error" && tasks.length === 0);
    const compact = waiting || step === "questions" || (step === "tasks" && (phase === "error" || (phase === "done" && tasks.length === 0)));
    const statusText = phase === "reading"
        ? "Reading the document and planning the work…"
        : total > 0 ? `Writing tasks — ${Math.min(tasks.length, total)} of ${total}` : "Writing tasks…";

    useEffect(() => {
        if (selectAllRef.current) selectAllRef.current.indeterminate = !allKept && kept.length > 0;
    }, [allKept, kept.length]);

    const regenerate = () => {
        setPrompt(null);
        try { sessionStorage.removeItem(cacheKey); } catch (e) { /* ignore */ }
        freshRunRef.current = true;
        setAttempt((a) => a + 1);
    };

    const finish = (data) => {
        message.success(
            `${data.created} tasks added to ${projectName}` +
            (data.planning_hold ? " — On Hold until the project leaves Planning" : "") +
            (data.project_due_moved_to ? `. Project due date moved to ${moment(data.project_due_moved_to).format("DD MMM YYYY")}` : "")
        );
        try { sessionStorage.removeItem(cacheKey); sessionStorage.removeItem(questionsKey); } catch (e) { /* ignore */ }
        window.dispatchEvent(new CustomEvent("planit:projectsUpdated", { detail: { project_id: projectId } }));
        onClose();
    };

    const createTasks = () => {
        if (kept.length === 0) return;
        // Same required fields as the Create Task form; missing ones turn red as they do there
        const missingByKey = {};
        let first = null;
        for (const t of kept) {
            const missing = [];
            if (!t.title?.trim()) missing.push("title");
            if (!t.assign_to) missing.push("assigned to");
            if (!t.status) missing.push("status");
            if (engine && !(Number(t.effort_value) > 0)) missing.push("effort");
            if (!engine && !t.due_date) missing.push("due date");
            if (missing.length) {
                missingByKey[t.key] = missing;
                if (!first) first = { t, missing };
            }
        }
        if (first) {
            setInvalid(missingByKey);
            setFormError(`Task ${tasks.indexOf(first.t) + 1}: please fill in ${first.missing.join(", ")}`);
            document.getElementById(`ai-task-${first.t.key}`)?.scrollIntoView({ block: "center", behavior: "smooth" });
            return;
        }
        setCreating(true);
        setFormError("");
        const body = kept.map((t) => ({
            title: t.title,
            description: t.description,
            assign_to: t.assign_to,
            status: t.status,
            approval_required: t.approval_required,
            task_priority: t.task_priority,
            effort_value: t.effort_value,
            effort_unit: t.effort_unit,
            due_date: t.due_date,
        }));
        ProjectServices.aiCreateTasks(projectId, body, DataParser.CompanyIdentifier())
            .then((res) => {
                const data = res?.data || {};
                if (data.failed?.length) { setPrompt({ type: "failed", items: data.failed, data: res?.Good ? data : null }); return; }
                if (!res?.Good) { setFormError(typeof res?.data === "string" ? res.data : "Failed to create tasks"); return; }
                finish(data);
            })
            .catch((err) => setFormError(err?.response?.data?.data || "Failed to create tasks"))
            .finally(() => setCreating(false));
    };

    const menuProps = { menu, setMenu, disabled: !editable };
    const pillBorder = (bad, filled, open) => (bad ? "#ff4d4f" : (open || filled) ? "#1D1D1D" : "#E8E8E8");
    const pillColor = (bad, filled) => (filled ? "#1D1D1D" : bad ? "#ff4d4f" : "#A0A0A0");

    return ReactDOM.createPortal(
        <div style={{ position: "fixed", inset: 0, zIndex: MODAL_Z, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}>
            <style>{`
                .ai-review .ai-title-wrap { position: relative; }
                .ai-review .ai-title-wrap input {
                    width: 100%; border-radius: 12px !important; height: 40px !important; min-height: 0 !important; padding: 0 16px !important;
                    font-size: 15px !important; font-weight: 500 !important; color: #1D1D1D; background: transparent;
                    border: 1.5px solid transparent !important; box-shadow: none !important; outline: none; transition: all 0.2s ease !important;
                }
                .ai-review .ai-title-wrap input::placeholder { color: #a0a0a0 !important; }
                .ai-review .ai-title-wrap input:hover, .ai-review .ai-title-wrap input:focus { border-color: #1D1D1D !important; }
                .ai-review .ai-title-wrap.is-invalid input { border-color: #ff4d4f !important; }
                .ai-review .ai-title-wrap label {
                    position: absolute; left: 12px; top: 0; transform: translateY(-50%); font-size: 12px; color: #1D1D1D;
                    background: #fff; padding: 0 5px; margin: 0; opacity: 0; pointer-events: none; transition: all 0.2s ease; z-index: 2;
                }
                .ai-review .ai-title-wrap input:focus ~ label { opacity: 1; }

                .ai-review .ai-desc-wrap { position: relative; border-radius: 12px; border: 1.5px solid transparent; transition: all 0.2s ease; }
                .ai-review .ai-desc-wrap:hover, .ai-review .ai-desc-wrap:focus-within { border-color: #1D1D1D; }
                .ai-review .ai-desc-wrap textarea {
                    display: block; border: none !important; box-shadow: none !important; outline: none !important; resize: vertical;
                    background: transparent; border-radius: 12px; padding: 8px 16px 8px 40px; font-size: 13px; color: #1D1D1D; min-height: 0 !important;
                }
                .ai-review .ai-desc-wrap.has-value textarea, .ai-review .ai-desc-wrap:focus-within textarea { padding-left: 16px; }
                .ai-review .ai-desc-wrap .desc-icon { position: absolute; left: 16px; top: 10px; pointer-events: none; transition: all 0.2s ease; }
                .ai-review .ai-desc-wrap.has-value .desc-icon, .ai-review .ai-desc-wrap:focus-within .desc-icon { opacity: 0; }
                .ai-review .ai-desc-wrap label {
                    position: absolute; left: 36px; top: 8px; font-size: 13px; line-height: 20px; color: #a0a0a0; background: #fff;
                    padding: 0 5px; margin: 0; pointer-events: none; transition: all 0.2s ease; z-index: 2;
                }
                .ai-review .ai-desc-wrap:focus-within label { top: 0; transform: translateY(-50%); left: 12px; font-size: 12px; color: #1D1D1D; }
                .ai-review .ai-desc-wrap.has-value:not(:focus-within) label { opacity: 0; visibility: hidden; }

                .ai-review .ai-row { display: grid; grid-template-columns: 18px 26px 1fr; column-gap: 8px; row-gap: 6px; align-items: center; padding: 10px 0; border-bottom: 1px solid #f3f4f6; }
                .ai-review .ai-row > .ai-span { grid-column: 3; }
                .ai-review .ai-note { display: flex; gap: 8px; align-items: flex-start; background: #F5F5F5; border-radius: 8px; padding: 8px 12px; font-size: 12px; color: #555; margin-bottom: 8px; flex-shrink: 0; }
                .ai-review .ai-note > i { font-size: 15px; color: #1D1D1D; line-height: 1.2; }
                .ai-review .ai-link { color: #0DDFAB; font-weight: 600; cursor: pointer; }
                .ai-review .ai-question { padding: 12px 0; border-bottom: 1px solid #f3f4f6; }
                .ai-review .ai-q-progress { height: 4px; border-radius: 4px; background: #F0F0F0; overflow: hidden; }
                .ai-review .ai-q-progress > span { display: block; height: 100%; background: #0DDFAB; transition: width 0.25s ease; }
                .ai-review .ai-q-option {
                    display: flex; align-items: center; gap: 10px; padding: 10px 14px; border: 1.5px solid #E8E8E8; border-radius: 10px;
                    font-size: 13px; font-weight: 500; color: #1D1D1D; cursor: pointer; transition: all 0.15s ease;
                }
                .ai-review .ai-q-option:hover { border-color: #1D1D1D; }
                .ai-review .ai-q-option.is-picked { border-color: #0DDFAB; background: #E7FBF5; }
                .ai-review .ai-q-radio { width: 16px; height: 16px; border-radius: 50%; border: 1.5px solid #C8C8C8; flex-shrink: 0; position: relative; }
                .ai-review .ai-q-option.is-picked .ai-q-radio { border-color: #0DDFAB; }
                .ai-review .ai-q-option.is-picked .ai-q-radio::after { content: ""; position: absolute; inset: 3px; border-radius: 50%; background: #0DDFAB; }
                .ai-review .ai-loader {
                    position: relative; width: 56px; height: 56px; margin: 0 auto; border-radius: 50%; background: #E7FBF5;
                    display: flex; align-items: center; justify-content: center; font-size: 24px; color: #0DDFAB;
                }
                .ai-review .ai-loader::after {
                    content: ""; position: absolute; inset: -5px; border-radius: 50%; border: 3px solid transparent;
                    border-top-color: #0DDFAB; animation: ai-loader-spin 0.9s linear infinite;
                }
                @keyframes ai-loader-spin { to { transform: rotate(360deg); } }
                .ai-review .ai-q-text { font-size: 13px; font-weight: 600; color: #1D1D1D; margin-bottom: 8px; }
                .ai-review .action-btn-wrap.is-picked { border-color: #0DDFAB; background: #E7FBF5; }

                /* Create Task pills */
                .ai-review .action-btn-wrap {
                    display: flex; align-items: center; gap: 6px; padding: 4px 8px; border: 1px solid #E8E8E8; border-radius: 5px;
                    background: #fff; transition: all 0.2s ease; min-width: fit-content; height: 30px;
                }
                .ai-review .action-btn-wrap:hover, .ai-review .action-btn-wrap:focus-within { border-color: #1D1D1D; }
                .ai-review .cursor-pointer { cursor: pointer; }
                .ai-review input.effort-btn-input {
                    width: 28px !important; height: auto !important; min-height: 0 !important; border: none !important;
                    box-shadow: none !important; outline: none !important; background: transparent !important;
                }
                .ai-review .action-datepicker { width: 55px !important; margin-left: -3px !important; padding: 0 !important; }
                .ai-review .action-datepicker .ant-picker-input > input { font-size: 13px !important; font-weight: 600 !important; color: #000 !important; cursor: pointer !important; padding-left: 0 !important; }
                .ai-review .action-datepicker .ant-picker-input > input::placeholder { color: #a0a0a0 !important; }

                /* Create Task dropdowns. The Create Project / Create Task forms (mounted on every page) make
                   these absolute at top:100%; here they must take up their own height or the menu is
                   placed as a zero-height box and spills down over its button. */
                .ai-review-popup .custom-user-dropdown, .ai-review-popup .custom-priority-dropdown {
                    position: static !important; margin-top: 0 !important;
                }
                .ai-review-popup .custom-user-dropdown { width: 250px; background: #fff; border: 1px solid #E8E8E8; border-radius: 10px; box-shadow: 0 10px 30px rgba(0,0,0,0.1); overflow: hidden; }
                .ai-review-popup .search-header { padding: 12px 16px; border-bottom: 1px solid #F0F0F0; display: flex; align-items: center; gap: 12px; }
                .ai-review-popup .search-header i { font-size: 18px; color: #1a1a1a; }
                .ai-review-popup .search-header input { border: none !important; outline: none !important; box-shadow: none !important; font-size: 14px; width: 100%; color: #1D1D1D; font-weight: 400; }
                .ai-review-popup .user-list-scroll { padding: 5px; max-height: 250px; overflow-y: auto; }
                .ai-review-popup .user-option-item { display: flex; align-items: center; gap: 10px; padding: 8px 10px; border-radius: 6px; cursor: pointer; transition: all 0.2s ease; font-size: 11px; color: #1a1a1a; }
                .ai-review-popup .user-option-item:hover { background: #f0fdf9; }
                .ai-review-popup .user-avatar-mini { width: 20px; height: 20px; background-color: #0DDFAB; color: #000; border-radius: 4px; display: flex; align-items: center; justify-content: center; font-size: 9px; font-weight: 700; }
                .ai-review-popup .no-users-found { padding: 12px; font-size: 12px; color: #A0A0A0; text-align: center; }
                .ai-review-popup .custom-priority-dropdown { background: #fff; border: 1px solid #E8E8E8; border-radius: 10px; box-shadow: 0 10px 30px rgba(0,0,0,0.1); overflow: hidden; }
                .ai-review-popup .priority-list-scroll { padding: 5px; }
                .ai-review-popup .priority-option-item { display: flex; align-items: center; padding: 8px 10px; border-radius: 6px; cursor: pointer; transition: all 0.2s ease; font-size: 11px; color: #1a1a1a; font-weight: 500; }
                .ai-review-popup .priority-option-item:hover { background: #f0fdf9; }

                /* Create Task buttons */
                .btn-create-task {
                    background-color: #0DDFAB; border: none; border-radius: 5px; height: 30px; padding: 0 20px; color: #000; font-weight: 700;
                    font-size: 13px; display: flex; align-items: center; justify-content: center; transition: all 0.2s ease; box-shadow: none !important; flex-shrink: 0 !important;
                }
                .btn-create-task:hover { background-color: #0bc698; transform: translateY(-2px); }
                .btn-create-task:active { transform: translateY(0); }
                .btn-create-task:disabled { opacity: 0.6; cursor: not-allowed; transform: none; box-shadow: none; }
                .ai-btn-secondary {
                    height: 30px; padding: 0 16px; border: 1px solid #E8E8E8 !important; background: #F2F2F2; color: #1D1D1D;
                    font-weight: 600; font-size: 13px; cursor: pointer; flex-shrink: 0;
                }
                .ai-btn-secondary:hover { border-color: #1D1D1D !important; }
            `}</style>

            <div className="modal-content ai-review" style={{ borderRadius: "24px", border: "none", padding: "10px 18px 20px 18px", boxShadow: "0 10px 40px rgba(0,0,0,0.12)", background: "#fff", width: compact ? "560px" : "1100px", maxWidth: "100%", height: compact ? "auto" : "92vh", display: "flex", flexDirection: "column", transition: "width 0.25s ease" }}>
                <div className="modal-header border-0 pb-2 d-flex justify-content-between align-items-start">
                    <div className="px-2">
                        <div style={{ fontSize: "16px", fontWeight: "700", color: "#1D1D1D" }}>AI task suggestions — {projectName}</div>
                        <div style={{ fontSize: "12px", color: "#A0A0A0" }}>
                            {step === "tasks" ? (
                                <>
                                    {detail === "low" ? "Low-level (detailed) tasks" : "High-level tasks"}
                                    {documentName ? ` from ${documentName}` : ""}. Review, edit or untick before creating.
                                </>
                            ) : (
                                <>Step 1 of 2 — a few questions first{documentName ? ` about ${documentName}` : ""}, so the tasks match what the client wants.</>
                            )}
                        </div>
                    </div>
                    <button type="button" className="btn-close shadow-none" aria-label="Close" onClick={onClose} style={{ fontSize: "18px", backgroundSize: "16px", opacity: 0.8 }} />
                </div>

                <div className="modal-body pt-0 px-4 pb-1" style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                    {waiting ? (
                        step === "questions-loading" ? (
                            <LoadingCard title="Reading your document" detail="Finding what it leaves unclear before any task is planned · about 20 seconds" />
                        ) : (
                            <LoadingCard title="Planning the tasks" detail={`${statusText} · usually under a minute`} percent={percent} />
                        )
                    ) : step !== "tasks" ? (
                        (() => {
                            const q = questions[qIndex];
                            if (!q) return null;
                            return (
                                <div className="px-1 pb-2">
                                    <div className="d-flex align-items-center justify-content-between mb-2" style={{ fontSize: "12px", color: "#A0A0A0", fontWeight: "600" }}>
                                        <span>Question {qIndex + 1} of {questions.length}</span>
                                        <span>{answeredList().length} answered</span>
                                    </div>
                                    <div className="ai-q-progress"><span style={{ width: `${((qIndex + 1) / questions.length) * 100}%` }} /></div>
                                    <div className="ai-q-text" style={{ fontSize: "15px", marginTop: "18px", marginBottom: "12px" }}>{q.question}</div>
                                    {q.options?.length > 0 && (
                                        <div className="d-flex flex-column gap-2 mb-3">
                                            {q.options.map((o) => (
                                                <div
                                                    key={o}
                                                    className={`ai-q-option ${answers[qIndex] === o ? "is-picked" : ""}`}
                                                    onClick={() => setAnswers((a) => ({ ...a, [qIndex]: a[qIndex] === o ? "" : o }))}
                                                >
                                                    <span className="ai-q-radio" />
                                                    <span>{o}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <div className={`ai-desc-wrap ${answers[qIndex] && !q.options?.includes(answers[qIndex]) ? "has-value" : ""}`} style={{ border: "1.5px solid #E8E8E8" }}>
                                        <textarea
                                            key={qIndex}
                                            rows={2}
                                            value={q.options?.includes(answers[qIndex]) ? "" : (answers[qIndex] || "")}
                                            onChange={(e) => setAnswers((a) => ({ ...a, [qIndex]: e.target.value }))}
                                        />
                                        <MaskIcon className="desc-icon" name="descriptionmodal.svg" color="#a0a0a0" />
                                        <label>{q.options?.length ? "Or type your own answer" : "Your answer"}</label>
                                    </div>
                                    <div style={{ fontSize: "12px", color: "#A0A0A0", marginTop: "8px" }}>
                                        Not sure? Skip it and the AI assumes the simplest option.
                                    </div>
                                </div>
                            );
                        })()
                    ) : (<>
                    {phase !== "done" && phase !== "error" && (
                        <div className="mb-3" style={{ flexShrink: 0 }}>
                            <Progress percent={percent} status="active" strokeColor="#0DDFAB" />
                            <div style={{ color: "#555", fontSize: "13px" }}>{statusText}</div>
                        </div>
                    )}

                    {phase === "error" ? (
                        <div className="text-center py-4">
                            <div className="mb-3" style={{ color: "#FF4D4F", fontSize: "13px" }}>{error}</div>
                            <button type="button" className="ai-btn-secondary d-inline-flex" onClick={() => setAttempt((a) => a + 1)}>Try again</button>
                        </div>
                    ) : editable && tasks.length === 0 ? (
                        <div className="text-center py-5" style={{ color: "#A0A0A0", fontSize: "13px" }}>The AI didn't find any tasks in this document.</div>
                    ) : (
                        <>
                            {editable && tasks.length > 0 && (
                                <div className="ai-note">
                                    <i className="uil uil-info-circle" />
                                    <span>
                                        {engine
                                            ? "Advanced Task Scheduling is on, so these are created like any task there: effort only — the scheduler sets each task's start and due date from the assignee's queue, shift and holidays."
                                            : newProjectDue
                                                ? `Due dates are planned from each task's effort (one person, 6 focused hours a day, Mon–Fri). That runs past the project's due date, so creating these tasks moves the project due date to ${moment(newProjectDue).format("DD MMM YYYY")}.`
                                                : "Due dates are planned from each task's effort (one person, 6 focused hours a day, Mon–Fri) and fit within the project's due date. Adjust any date before creating."}
                                    </span>
                                </div>
                            )}
                            {editable && openQuestions.length > 0 && (
                                <div className="ai-note" style={{ maxHeight: "30vh", overflowY: "auto" }}>
                                    <i className="uil uil-question-circle" />
                                    <div>
                                        {openQuestions.length} question{openQuestions.length === 1 ? "" : "s"} to confirm with the client — gaps the AI found in the document (the tasks assume the simplest answer).{" "}
                                        <span className="ai-link" onClick={() => setShowQuestions((v) => !v)}>{showQuestions ? "Hide" : "Show"}</span>
                                        {showQuestions && (
                                            <ul className="mb-0 mt-1 ps-3">{openQuestions.map((q, i) => <li key={i}>{q}</li>)}</ul>
                                        )}
                                    </div>
                                </div>
                            )}
                            <div style={{ flex: 1, minHeight: 0, overflowY: "auto", paddingRight: "4px" }} onScroll={() => menu && setMenu(null)}>
                                {tasks.map((t, i) => {
                                    const bad = invalid[t.key] || [];
                                    const assignee = assigneeOptions.find((o) => o.value === t.assign_to);
                                    const status = STATUS_OPTIONS.find((o) => o.value === (t.status || "Pending"));
                                    const hasEffort = Number(t.effort_value) > 0;
                                    return (
                                        <div key={t.key} id={`ai-task-${t.key}`} className="ai-row" style={{ opacity: t.keep ? 1 : 0.45, pointerEvents: editable ? undefined : "none" }}>
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                checked={t.keep}
                                                disabled={!editable}
                                                onChange={(e) => update(t.key, { keep: e.target.checked })}
                                                style={{ borderRadius: "5px", cursor: "pointer", margin: 0, pointerEvents: "auto" }}
                                            />
                                            <span style={{ color: "#A0A0A0", fontSize: "13px", textAlign: "right" }}>{i + 1}.</span>
                                            <div className={`ai-title-wrap ${bad.includes("title") ? "is-invalid" : ""}`}>
                                                <input
                                                    type="text"
                                                    id={`ai-title-${t.key}`}
                                                    placeholder="Enter a task title"
                                                    value={t.title}
                                                    maxLength={200}
                                                    disabled={!editable}
                                                    onChange={(e) => update(t.key, { title: e.target.value })}
                                                />
                                                <label htmlFor={`ai-title-${t.key}`}>Enter a task title</label>
                                            </div>

                                            {/* Same pills as the Create Task form for this task mode */}
                                            <div className="ai-span d-flex flex-wrap gap-1 align-items-center ps-1">
                                                <MenuAnchor
                                                    {...menuProps} id={`${t.key}:assign`} title="Assigned To"
                                                    className="action-btn-wrap cursor-pointer"
                                                    style={{ borderColor: pillBorder(bad.includes("assigned to"), !!assignee, menu === `${t.key}:assign`) }}
                                                    render={(close) => <UserMenu options={assigneeOptions} onSelect={(v) => { update(t.key, { assign_to: v }); close(); }} />}
                                                >
                                                    <MaskIcon name="assignedtomodal.svg" color={pillColor(bad.includes("assigned to"), !!assignee)} />
                                                    <span style={{ fontSize: "11px", fontWeight: "600", color: pillColor(bad.includes("assigned to"), !!assignee) }}>{assignee?.label || "Assigned to"}</span>
                                                </MenuAnchor>

                                                {engine ? (
                                                    <Tooltip title="Assign Effort" placement="top" overlayStyle={{ zIndex: POPUP_Z }}>
                                                        <div
                                                            className="action-btn-wrap"
                                                            style={{ borderColor: pillBorder(bad.includes("effort"), hasEffort), cursor: "text", gap: "4px" }}
                                                            onClick={(e) => { if (!e.target.closest(".effort-unit-toggle")) e.currentTarget.querySelector("input")?.focus(); }}
                                                        >
                                                            <MaskIcon name="runtimemodal.svg" color={pillColor(bad.includes("effort"), hasEffort)} />
                                                            <MenuAnchor
                                                                {...menuProps} id={`${t.key}:unit`}
                                                                className="effort-unit-toggle"
                                                                style={{ display: "flex", alignItems: "center" }}
                                                                render={(close) => <PlainMenu options={UNIT_OPTIONS} selected={t.effort_unit || "hours"} minWidth="95px" fontSize="12px" onSelect={(v) => { changeUnit(t, v); close(); }} />}
                                                            >
                                                                <span style={{ fontSize: "10px", fontWeight: "600", color: hasEffort ? "#1D1D1D" : "#A0A0A0", cursor: "pointer", userSelect: "none", borderRight: "1px solid #E8E8E8", paddingRight: "5px", lineHeight: 1, display: "flex", alignItems: "center", gap: "2px" }}>
                                                                    {UNIT_OPTIONS.find((u) => u.value === (t.effort_unit || "hours"))?.short}
                                                                    <span style={{ fontSize: "7px" }}>▾</span>
                                                                </span>
                                                            </MenuAnchor>
                                                            <input
                                                                type="number"
                                                                className="no-spinner effort-btn-input"
                                                                placeholder={UNIT_OPTIONS.find((u) => u.value === (t.effort_unit || "hours"))?.short}
                                                                min="0"
                                                                step={(t.effort_unit || "hours") === "hours" ? "0.5" : "1"}
                                                                disabled={!editable}
                                                                value={t.effort_value ?? ""}
                                                                onWheel={(e) => e.target.blur()}
                                                                onChange={(e) => update(t.key, { effort_value: e.target.value })}
                                                                style={{ fontSize: "11px", fontWeight: "600", color: hasEffort ? "#1D1D1D" : "#A0A0A0", padding: 0, fontFamily: "inherit", cursor: "text" }}
                                                            />
                                                        </div>
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip title="Due Date" placement="top" overlayStyle={{ zIndex: POPUP_Z }}>
                                                        <div className="action-btn-wrap" style={{ borderColor: pillBorder(bad.includes("due date"), !!t.due_date) }}>
                                                            <MaskIcon name="duedatemodal.svg" color={pillColor(bad.includes("due date"), !!t.due_date)} />
                                                            <DatePicker
                                                                value={t.due_date ? moment(t.due_date, "YYYY-MM-DD") : null}
                                                                onChange={(d) => update(t.key, { due_date: d ? d.format("YYYY-MM-DD") : null })}
                                                                format="DD MMM"
                                                                placeholder={editable ? "Due Date" : "…"}
                                                                className="action-datepicker"
                                                                bordered={false}
                                                                allowClear={false}
                                                                suffixIcon={null}
                                                                disabled={!editable}
                                                                popupStyle={{ zIndex: POPUP_Z }}
                                                                disabledDate={(d) => d && (d.isBefore(moment(), "day") || (projectDue && d.isAfter(moment(projectDue), "day")))}
                                                            />
                                                        </div>
                                                    </Tooltip>
                                                )}

                                                {engine && (
                                                    <MenuAnchor
                                                        {...menuProps} id={`${t.key}:priority`} title="Select Priority"
                                                        className="action-btn-wrap cursor-pointer" style={{ borderColor: "#1D1D1D" }}
                                                        render={(close) => <PlainMenu options={PRIORITY_OPTIONS} selected={t.task_priority || "normal"} minWidth="140px" fontSize="13px" onSelect={(v) => { update(t.key, { task_priority: v }); close(); }} />}
                                                    >
                                                        <span style={{ display: "inline-block", width: "8px", height: "8px", borderRadius: "50%", flexShrink: 0, backgroundColor: t.task_priority === "critical" ? "#FF4D4F" : "#0DDFAB" }} />
                                                        <span style={{ fontSize: "11px", fontWeight: "600", color: "#1D1D1D" }}>{t.task_priority === "critical" ? "Critical" : "Normal"}</span>
                                                    </MenuAnchor>
                                                )}

                                                <MenuAnchor
                                                    {...menuProps} id={`${t.key}:status`} title="Task Status"
                                                    className="action-btn-wrap cursor-pointer"
                                                    style={{ borderColor: pillBorder(bad.includes("status"), !!t.status, menu === `${t.key}:status`) }}
                                                    render={(close) => <OptionMenu options={STATUS_OPTIONS} width="160px" onSelect={(v) => { update(t.key, { status: v }); close(); }} />}
                                                >
                                                    <MaskIcon name="taskstatusmodal.svg" color={pillColor(bad.includes("status"), !!t.status)} />
                                                    <span style={{ fontSize: "11px", fontWeight: "600", color: pillColor(bad.includes("status"), !!t.status) }}>{status?.label || "Task Status"}</span>
                                                </MenuAnchor>

                                                <MenuAnchor
                                                    {...menuProps} id={`${t.key}:approval`} title="Approval Required"
                                                    className="action-btn-wrap cursor-pointer" style={{ borderColor: "#1D1D1D" }}
                                                    render={(close) => <OptionMenu options={APPROVAL_OPTIONS} width="120px" onSelect={(v) => { update(t.key, { approval_required: v }); close(); }} />}
                                                >
                                                    <MaskIcon name="approvalrequiredmodal.svg" color="#1D1D1D" />
                                                    <span style={{ fontSize: "11px", fontWeight: "600", color: "#1D1D1D" }}>{t.approval_required ? "Yes" : "No"}</span>
                                                </MenuAnchor>
                                            </div>

                                            <div className={`ai-span ai-desc-wrap ${t.description ? "has-value" : ""}`}>
                                                <textarea
                                                    value={t.description || ""}
                                                    disabled={!editable}
                                                    // Sized from the text: the global `textarea { width: 100% !important }` breaks auto-size measuring
                                                    rows={Math.min(9, Math.max(2, (t.description || "").split("\n").reduce((n, line) => n + Math.max(1, Math.ceil(line.length / 120)), 0)))}
                                                    onChange={(e) => update(t.key, { description: e.target.value })}
                                                />
                                                <MaskIcon className="desc-icon" name="descriptionmodal.svg" color="#a0a0a0" />
                                                <label>Description</label>
                                            </div>
                                        </div>
                                    );
                                })}
                                <div ref={listEndRef} />
                            </div>
                        </>
                    )}
                    </>)}
                </div>

                {step === "questions" && (
                    <div className="d-flex align-items-center justify-content-between gap-2 mx-4 pt-3 border-top">
                        <button type="button" className="ai-btn-secondary" style={{ background: "transparent" }} onClick={() => { setAnswers({}); startTasks(); }}>Skip all</button>
                        <div className="d-flex align-items-center gap-2">
                            {qIndex > 0 && (
                                <button type="button" className="ai-btn-secondary" onClick={() => setQIndex((i) => i - 1)}>
                                    <i className="uil uil-arrow-left me-1" />Back
                                </button>
                            )}
                            {!String(answers[qIndex] || "").trim() && (
                                <button type="button" className="ai-btn-secondary" onClick={() => (qIndex < questions.length - 1 ? setQIndex((i) => i + 1) : startTasks())}>Skip</button>
                            )}
                            {qIndex < questions.length - 1 ? (
                                <button type="button" className="btn-create-task" disabled={!String(answers[qIndex] || "").trim()} onClick={() => setQIndex((i) => i + 1)}>
                                    Next<i className="uil uil-arrow-right ms-1" style={{ fontSize: "16px" }} />
                                </button>
                            ) : (
                                <button type="button" className="btn-create-task" onClick={startTasks}>
                                    <i className="uil uil-bolt me-1" style={{ fontSize: "16px" }} />Generate Tasks
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {step === "tasks" && editable && (
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mx-4 pt-2 border-top">
                        <div className="d-flex align-items-center gap-3">
                            <label className="d-flex align-items-center gap-2 m-0 cursor-pointer" style={{ fontSize: "13px", fontWeight: "600", color: "#1D1D1D" }}>
                                <input
                                    ref={selectAllRef}
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={allKept}
                                    onChange={(e) => setTasks((prev) => prev.map((t) => ({ ...t, keep: e.target.checked })))}
                                    style={{ borderRadius: "5px", cursor: "pointer", margin: 0 }}
                                />
                                Select all
                            </label>
                            {/* Opens upwards from the footer, placed by CSS like Create Task's Recurring menu */}
                            <div className="ai-assign-all position-relative">
                                <Tooltip title="Assign every ticked task to one person" placement="top" overlayStyle={{ zIndex: POPUP_Z }}>
                                    <div className="action-btn-wrap cursor-pointer" onClick={() => setMenu(menu === "all:assign" ? null : "all:assign")}>
                                        <MaskIcon name="assignedtomodal.svg" color="#A0A0A0" />
                                        <span style={{ fontSize: "11px", fontWeight: "600", color: "#A0A0A0" }}>Assign all ticked to…</span>
                                    </div>
                                </Tooltip>
                                {menu === "all:assign" && (
                                    <div className="ai-review-popup" style={{ position: "absolute", left: 0, bottom: "calc(100% + 5px)", zIndex: 10 }}>
                                        <UserMenu options={assigneeOptions} onSelect={(v) => { setTasks((prev) => prev.map((t) => (t.keep ? { ...t, assign_to: v } : t))); setInvalid({}); setMenu(null); }} />
                                    </div>
                                )}
                            </div>
                            {formError && <span style={{ color: "#FF4D4F", fontSize: "12px" }}>{formError}</span>}
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            {questions.length > 0 && (
                                <button type="button" className="ai-btn-secondary" onClick={editAnswers}>Edit answers</button>
                            )}
                            <button type="button" className="ai-btn-secondary" onClick={() => setPrompt({ type: "regenerate" })}>Regenerate</button>
                            <button type="button" className="ai-btn-secondary" onClick={onClose}>Skip</button>
                            <button type="button" className="btn-create-task" disabled={creating || kept.length === 0} onClick={createTasks}>
                                {creating ? (
                                    <div className="spinner-border spinner-border-sm me-2" role="status" />
                                ) : (
                                    <i className="uil uil-plus me-1" style={{ fontSize: "16px" }} />
                                )}
                                Create {kept.length} {kept.length === 1 ? "Task" : "Tasks"}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {prompt?.type === "regenerate" && (
                <PromptCard
                    title="Regenerate the tasks?"
                    onClose={() => setPrompt(null)}
                    actions={<>
                        <button type="button" className="ai-btn-secondary" onClick={() => setPrompt(null)}>Cancel</button>
                        <button type="button" className="btn-create-task" onClick={regenerate}>Regenerate</button>
                    </>}
                >
                    Your edits are discarded and the AI reads the document again (about a minute).
                </PromptCard>
            )}
            {prompt?.type === "failed" && (
                <PromptCard
                    title={`${prompt.items.length} task${prompt.items.length === 1 ? "" : "s"} could not be created`}
                    onClose={() => (prompt.data ? finish(prompt.data) : setPrompt(null))}
                    actions={<button type="button" className="btn-create-task" onClick={() => (prompt.data ? finish(prompt.data) : setPrompt(null))}>OK</button>}
                >
                    <ul className="mb-0 ps-3">{prompt.items.map((f, i) => <li key={i}>{f}</li>)}</ul>
                </PromptCard>
            )}
        </div>,
        document.body
    );
};

export default AiTaskReviewModal;
